import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Clock,
  Download,
  FileCheck2,
  Filter,
  KeyRound,
  Lock,
  LogOut,
  Mail,
  Printer,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { generateAndDownloadSecurityDocPdf } from '@generated/lib/securityDocPdf';
import {
  getSecurityActivityLogs,
  MONITORED_BOARDS,
  type SecurityActivityLogsResponse,
} from '@generated/server/activityLogs';
import { verifyAdminEmail, verifyAdminSecret } from '@generated/server/adminAuth';

export const Route = createFileRoute('/_app/security-doc')({
  validateSearch: (search: Record<string, unknown>) => ({
    admin: search.admin ? String(search.admin) : undefined,
  }),
  component: ProtectedSecurityDocPage,
});

type TabView = 'spec' | 'logs';

const SESSION_STORAGE_KEY = 'tau_sec_admin_token';
const SESSION_EMAIL_KEY = 'tau_sec_admin_email';

export function ProtectedSecurityDocPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/security-doc' });

  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState<string>('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Auth flow states
  const [authStep, setAuthStep] = useState<1 | 2>(1);
  const [secretInput, setSecretInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Content states
  const [tab, setTab] = useState<TabView>('spec');
  const [downloading, setDownloading] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<number>(30);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [logsData, setLogsData] = useState<SecurityActivityLogsResponse | null>(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    try {
      const storedToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
      const storedEmail = sessionStorage.getItem(SESSION_EMAIL_KEY);
      if (storedToken) {
        setAdminToken(storedToken);
        if (storedEmail) setAdminEmail(storedEmail);
        setIsCheckingAuth(false);
        return;
      }
    } catch {
      // sessionStorage not available
    }

    // If no admin flag in query search, perform silent redirect to home
    if (!search.admin) {
      navigate({ to: '/' });
      return;
    }

    setIsCheckingAuth(false);
  }, [search.admin, navigate]);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secretInput.trim()) {
      setAuthError('נא להזין סיסמת מנהל סודית.');
      return;
    }

    setVerifying(true);
    setAuthError(null);
    try {
      const res = await verifyAdminSecret({ data: { secretKey: secretInput.trim() } });
      if (!res.success) {
        setAuthError(res.message);
        return;
      }
      setAuthStep(2);
      toast.success('שלב א׳ הושלם: סיסמת מנהל אומתה');
    } catch (err) {
      setAuthError('שגיאה בבדיקת סיסמת מנהל.');
    } finally {
      setVerifying(false);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      setAuthError('נא להזין את כתובת הדוא״ל המורשית.');
      return;
    }

    setVerifying(true);
    setAuthError(null);
    try {
      const res = await verifyAdminEmail({
        data: {
          secretKey: secretInput.trim(),
          email: emailInput.trim(),
        },
      });

      if (!res.success || !res.token) {
        setAuthError(res.message || 'אימות נכשל.');
        return;
      }

      setAdminToken(res.token);
      setAdminEmail(res.email || emailInput.trim());
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, res.token);
        sessionStorage.setItem(SESSION_EMAIL_KEY, res.email || emailInput.trim());
      } catch {
        // ignore
      }
      toast.success('אימות מנהל דו-שלבי הושלם בהצלחה!');
    } catch (err) {
      setAuthError('שגיאה במהלך אימות הדוא״ל.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_EMAIL_KEY);
    } catch {
      // ignore
    }
    setAdminToken(null);
    toast.info('התנתקת מאזור הניהול');
    navigate({ to: '/' });
  };

  const fetchLogs = async () => {
    if (!adminToken) return;
    setLoadingLogs(true);
    try {
      const res = await getSecurityActivityLogs({
        data: {
          adminToken,
          boardId: selectedBoardId || undefined,
          days: selectedDays,
          limit: 100,
        },
      });
      if (res.error) {
        toast.error(res.error);
        if (res.error.includes('הרשאת גישה')) {
          handleLogout();
        }
      } else {
        setLogsData(res);
      }
    } catch (err) {
      console.error('Failed to load activity logs', err);
      toast.error('שגיאה בטעינת יומן הפעילות מהלוחות');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (adminToken && tab === 'logs') {
      fetchLogs();
    }
  }, [adminToken, tab, selectedBoardId, selectedDays]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      await generateAndDownloadSecurityDocPdf();
      toast.success('מסמך מפרט האבטחה הורד בהצלחה (PDF)', {
        description: 'TAU_Engineering_Forms_Security_Specifications.pdf',
      });
    } catch (err) {
      toast.error('שגיאה בהורדת קובץ ה-PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (isCheckingAuth) {
    return null;
  }

  // Not authenticated: render clean Two-Step Auth Modal
  if (!adminToken) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-2 text-primary mb-2">
            <Lock className="size-5" />
            <span className="text-xs font-bold uppercase tracking-wider">אזור ניהול מוגן (Restricted)</span>
          </div>

          <h1 className="text-xl font-bold text-foreground sm:text-2xl">
            אימות מנהל דו-שלבי
          </h1>
          <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
            גישה ליומני הפעילות ומפרט האבטחה מוגבלת למנהל המערכת בלבד (דותן דקל).
          </p>

          <div className="mt-6 flex items-center gap-2 border-b border-[hsl(var(--rule-soft))] pb-3 text-xs">
            <div
              className={`flex items-center gap-1.5 font-semibold ${
                authStep === 1 ? 'text-primary' : 'text-emerald-600 dark:text-emerald-400'
              }`}
            >
              <span className="flex size-5 items-center justify-center rounded-full border border-current text-[10px]">
                {authStep > 1 ? '✓' : '1'}
              </span>
              <span>שלב 1: סיסמת מנהל</span>
            </div>
            <span className="text-muted-foreground">←</span>
            <div
              className={`flex items-center gap-1.5 font-semibold ${
                authStep === 2 ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <span className="flex size-5 items-center justify-center rounded-full border border-current text-[10px]">
                2
              </span>
              <span>שלב 2: אימות דוא״ל</span>
            </div>
          </div>

          {authError && (
            <div className="mt-4 flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              <ShieldAlert className="size-4 shrink-0" />
              <span>{authError}</span>
            </div>
          )}

          {authStep === 1 ? (
            <form onSubmit={handleStep1Submit} className="mt-5 space-y-4">
              <div>
                <label htmlFor="admin-secret" className="block text-xs font-medium text-foreground">
                  סיסמת מנהל סודית (Admin Secret Password)
                </label>
                <div className="relative mt-1.5">
                  <KeyRound className="pointer-events-none absolute right-3 top-3 size-4 text-muted-foreground" />
                  <input
                    id="admin-secret"
                    type="password"
                    value={secretInput}
                    onChange={(e) => setSecretInput(e.target.value)}
                    placeholder="הזן סיסמת מנהל סודית"
                    className="h-10 w-full border border-[hsl(var(--rule))] bg-[hsl(var(--field-bg))] pr-9 pl-3 text-sm text-foreground outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate({ to: '/' })}
                  className="h-10 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-4 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  ביטול וחזרה
                </button>
                <button
                  type="submit"
                  disabled={verifying}
                  className="h-10 border border-primary bg-primary px-5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {verifying ? 'מאמת...' : 'המשך לאימות דוא״ל ←'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleStep2Submit} className="mt-5 space-y-4">
              <div>
                <label htmlFor="admin-email" className="block text-xs font-medium text-foreground">
                  כתובת דוא״ל מורשית (דותן דקל)
                </label>
                <div className="relative mt-1.5">
                  <Mail className="pointer-events-none absolute right-3 top-3 size-4 text-muted-foreground" />
                  <input
                    id="admin-email"
                    type="email"
                    dir="ltr"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="dotand@mail.tau.ac.il"
                    className="h-10 w-full border border-[hsl(var(--rule))] bg-[hsl(var(--field-bg))] pr-9 pl-3 text-sm text-foreground outline-none focus:border-primary text-start"
                  />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  הזן את כתובת הדוא״ל האקדמית שלך באוניברסיטת תל אביב.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setAuthStep(1)}
                  className="h-10 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  חזרה לשלב 1
                </button>
                <button
                  type="submit"
                  disabled={verifying}
                  className="h-10 border border-primary bg-primary px-5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {verifying ? 'מאמת מנהל...' : 'כניסה לאזור הניהול ✓'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const layers = [
    {
      id: 'layer-1',
      number: '01',
      title: 'סניטציה, ניקוי קלט ו-Escape Strings',
      titleEn: 'Input Sanitization & Injection Defense',
      icon: Shield,
      badge: 'פעיל בכל הטפסים',
      points: [
        'טיהור תגיות ומניעת XSS: הסרה אוטומטית של תגיות <script>, <iframe>, <style> ותווי בקרה מכל שדות הטקסט.',
        'קידוד פרמטרים בטוח: שימוש במשתני GraphQL מבודדים (Variables) המונעים שיבוש שאילתות ו-GraphQL Injections.',
        'דחיסת רווחים: ניקוי רווחי קצה ודחיסת רווחים כפולים בכל קלט.',
      ],
    },
    {
      id: 'layer-2',
      number: '02',
      title: 'אימות סכמה קפדני (Zod Validation Engine)',
      titleEn: 'Strict Field & Identity Validation',
      icon: FileCheck2,
      badge: 'Zod v4 Engine',
      points: [
        'תעודת זהות / דרכון: תמיכה באות פותחת ולאחריה 4 עד 15 ספרות, עם ניקוי מקפים ורווחים.',
        'הגבלת שמות לעד 10 תווים: הגבלה פיזית של maxLength={10} על שמות פרטיים, שמות משפחה ושמות באנגלית.',
        'מזהה קורס נומרי: אכיפה של ספרות בלבד במספרי קורסים.',
        'אימות דוא"ל תקני: בדיקת תקינות מבנה אימייל סטנדרטי וחוקי.',
        'חוויית שגיאות נוחה: השגיאות מוצגות רק בעת מעבר שלב או לחיצה על הגשה.',
      ],
    },
    {
      id: 'layer-3',
      number: '03',
      title: 'הגבלת קצב בקשות (Rate Limiting)',
      titleEn: 'Spam, DoS & Brute-Force Rate Limiting',
      icon: Zap,
      badge: 'שרת Backend מוגן',
      points: [
        'הגבלת הגשות טפסים: חסימה בצד השרת של מעל 5 הגשות טופס בשעה לכל מזהה / כתובת.',
        'הגבלת בדיקות ומכסות: חסימה של מעל 20 בקשות בדקה עבור קודי OTP, בדיקות כפילות ומכסות.',
        'הודעת חסימה מובנית: "בוצעו ניסיונות מרובים מדי. אנא המתן מספר דקות לפני ביצוע ניסיון נוסף."',
      ],
    },
    {
      id: 'layer-4',
      number: '04',
      title: 'הגנת בוטים חכמה (Cloudflare Turnstile)',
      titleEn: 'Cloudflare Turnstile Bot Protection',
      icon: Lock,
      badge: 'Site Key מחובר',
      points: [
        'אימות אנושי שקוף: בדיקה אוטומטית וחכמה ללא פאזלים או תמונות.',
        'אימות צד שרת ישיר: שליחת הטוקן לבדיקה מול ה-API של Cloudflare עם ה-Secret Key לפני כל כתיבה ללוח.',
        'Site Key פעיל: 0x4AAAAAAFLGTay8lcCWkIHH.',
      ],
    },
    {
      id: 'layer-5',
      number: '05',
      title: 'אימות זהות דו-שלבי ואיפוס עמודות (2FA OTP)',
      titleEn: 'Two-Factor Verification & Column Clearance',
      icon: KeyRound,
      badge: 'אימות מדויק ואיפוס',
      points: [
        'הצלבת ת.ז. ודוא"ל: בדיקה קפדנית של שני הנתונים מול לוח ה-Monday טרם שליחת קוד.',
        'קוד בן 6 ספרות ותוקף 10 דקות: נעילה אוטומטית לאחר 5 ניסיונות שגויים.',
        'איפוס עמודות אוטומטי: ניקוי מיידי של שדות קוד האימות, החתימה והסטטוס מהלוח ברגע האימות.',
      ],
    },
  ];

  const filteredLogs = (logsData?.logs ?? []).filter((log) => {
    if (selectedCategory === 'all') return true;
    return log.eventCategory === selectedCategory;
  });

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 lg:py-12">
      {/* Top Admin Status Bar */}
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 text-xs">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-emerald-600 dark:text-emerald-400" />
          <span className="font-semibold text-foreground">
            אזור ניהול מאובטח · מחובר כמנהל: <span className="font-mono text-primary">{adminEmail || 'דותן דקל'}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-destructive hover:underline"
        >
          <LogOut className="size-3.5" />
          יציאה מאזור הניהול
        </button>
      </div>

      {/* Header Banner */}
      <header className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-6 sm:p-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              <ShieldCheck className="size-3.5" />
              מפרט אבטחה ויומן ביקורת רשמי (סודי - מנהל בלבד)
            </div>
            <h1 className="mt-3 text-2xl font-bold text-foreground sm:text-4xl">
              מערך הגנת הסייבר ובקרת הפעילות
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              אוניברסיטת תל אביב · הפקולטה להנדסה ע"ש איבי ואלדר פליישמן
              <br />
              תיעוד 5 שכבות ההגנה וניתוח דפוסי הגשה ואירועי אבטחה היסטוריים מלוחות ה-Monday.
            </p>
          </div>

          <div className="no-print flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="inline-flex h-11 items-center gap-2 border border-primary bg-primary px-4 text-xs font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              <Download className="size-4" />
              {downloading ? 'מייצר קובץ PDF...' : 'הורדת מפרט אבטחה (PDF)'}
            </button>

            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-11 items-center gap-2 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-4 text-xs font-medium text-foreground transition-colors hover:bg-[hsl(var(--surface-3))]"
            >
              <Printer className="size-4" />
              הדפסה
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="no-print mt-8 flex border-b border-[hsl(var(--rule))]">
          <button
            type="button"
            onClick={() => setTab('spec')}
            className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-colors ${
              tab === 'spec'
                ? 'border-primary text-primary font-bold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <ShieldCheck className="size-4" />
            <span>מפרט 5 שכבות האבטחה (Specifications)</span>
          </button>
          <button
            type="button"
            onClick={() => setTab('logs')}
            className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-colors ${
              tab === 'logs'
                ? 'border-primary text-primary font-bold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Activity className="size-4" />
            <span>יומן פעילות ודפוסי הגשה (Activity Logs & Patterns)</span>
          </button>
        </div>
      </header>

      {/* Tab 1: Security Specifications */}
      {tab === 'spec' && (
        <div className="mt-8 space-y-8">
          {/* Summary KPI Strip */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">מגבלת הגשות (Rate Limit)</span>
              <p className="mt-1 font-mono text-xl font-bold text-foreground">עד 5 בשעה</p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">הגנת בוטים (Bot Defense)</span>
              <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400">Turnstile Active</p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">הגבלת אורך שמות</span>
              <p className="mt-1 font-mono text-xl font-bold text-foreground">10 תווים</p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">מנגנון אימות (2FA OTP)</span>
              <p className="mt-1 text-xl font-bold text-foreground">6 ספרות / 10 דק'</p>
            </div>
          </div>

          {/* 5 Security Layers Cards */}
          <div className="space-y-5">
            <h2 className="text-lg font-bold text-foreground">פירוט 5 שכבות ההגנה והאבטחה</h2>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {layers.map((layer) => {
                const Icon = layer.icon;
                return (
                  <div
                    key={layer.id}
                    className="flex flex-col border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-5 shadow-2xs"
                  >
                    <div className="flex items-center justify-between border-b border-[hsl(var(--rule-soft))] pb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-primary">{layer.number}</span>
                        <Icon className="size-4 text-primary" />
                      </div>
                      <span className="border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                        {layer.badge}
                      </span>
                    </div>

                    <div className="mt-3 flex-1">
                      <h3 className="text-base font-semibold text-foreground">{layer.title}</h3>
                      <p dir="ltr" className="text-[11px] text-muted-foreground text-start">
                        {layer.titleEn}
                      </p>

                      <ul className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground">
                        {layer.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                            <span>{pt}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Security Matrix Table */}
          <div>
            <h2 className="text-lg font-bold text-foreground mb-4">מטריצת בקרות ואיומים (Security Matrix)</h2>
            <div className="overflow-x-auto border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))]">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-[hsl(var(--surface-2))] text-[11px] text-muted-foreground border-b border-[hsl(var(--rule))]">
                    <th className="p-3 text-start">שכבת הגנה</th>
                    <th className="p-3 text-start">מיקום הבקרה</th>
                    <th className="p-3 text-start">מנגנון הגנה</th>
                    <th className="p-3 text-start">איום מטופל</th>
                    <th className="p-3 text-center">סטטוס</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--rule-soft))]">
                  <tr>
                    <td className="p-3 font-semibold text-foreground">1. סניטציה ו-Escape Strings</td>
                    <td className="p-3 text-muted-foreground">לקוח + שרת (Client & Server)</td>
                    <td className="p-3 text-foreground">הסרת תגיות קוד ושימוש ב-GraphQL Variables</td>
                    <td className="p-3 text-muted-foreground">הזרקות XSS ושיבוש שאילתות</td>
                    <td className="p-3 text-center"><span className="text-emerald-600 font-bold">פעיל ✓</span></td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold text-foreground">2. אימות סכמה (Zod)</td>
                    <td className="p-3 text-muted-foreground">טפסים + שרת</td>
                    <td className="p-3 text-foreground">הגבלת 10 תווים לשמות, ת.ז. ואימייל תקני</td>
                    <td className="p-3 text-muted-foreground">הזנת נתונים פגומים ושיבוש לוחות</td>
                    <td className="p-3 text-center"><span className="text-emerald-600 font-bold">פעיל ✓</span></td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold text-foreground">3. הגבלת קצב (Rate Limiting)</td>
                    <td className="p-3 text-muted-foreground">שרת (Backend)</td>
                    <td className="p-3 text-foreground">5 הגשות/שעה, 20 בדיקות/דקה</td>
                    <td className="p-3 text-muted-foreground">הצפת שרת, מתקפות DoS וספאם</td>
                    <td className="p-3 text-center"><span className="text-emerald-600 font-bold">פעיל ✓</span></td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold text-foreground">4. אימות בוטים (Turnstile)</td>
                    <td className="p-3 text-muted-foreground">לקוח + Cloudflare API</td>
                    <td className="p-3 text-foreground">אימות אנושי שקוף ומאובטח</td>
                    <td className="p-3 text-muted-foreground">הגשות אוטומטיות ע"י בוטים</td>
                    <td className="p-3 text-center"><span className="text-emerald-600 font-bold">פעיל ✓</span></td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold text-foreground">5. אימות OTP ואיפוס עמודות</td>
                    <td className="p-3 text-muted-foreground">לוחות Monday + דוא"ל</td>
                    <td className="p-3 text-foreground">קוד 6 ספרות ואיפוס עמודות אוטומטי</td>
                    <td className="p-3 text-muted-foreground">התחזות, גניבת זהות ושימוש חוזר</td>
                    <td className="p-3 text-center"><span className="text-emerald-600 font-bold">פעיל ✓</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Activity Logs & Form Submission Patterns */}
      {tab === 'logs' && (
        <div className="mt-8 space-y-8">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Filter className="size-3.5" />
                <span>סינון:</span>
              </div>

              {/* Board Selector */}
              <select
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(e.target.value)}
                className="h-9 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3 text-xs text-foreground outline-none"
              >
                <option value="">כל הלוחות המנוטרים (4 לוחות)</option>
                {MONITORED_BOARDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>

              {/* Category Selector */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-9 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3 text-xs text-foreground outline-none"
              >
                <option value="all">כל סוגי האירועים</option>
                <option value="submission">הגשות טפסים חדשות (Submissions)</option>
                <option value="security">אירועי אבטחה, OTP וביטולים</option>
                <option value="status_update">עדכוני סטטוס ואישורים</option>
                <option value="system">עדכוני מערכת שוטפים</option>
              </select>

              {/* Date Window */}
              <select
                value={selectedDays}
                onChange={(e) => setSelectedDays(Number(e.target.value))}
                className="h-9 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3 text-xs text-foreground outline-none"
              >
                <option value={7}>7 ימים אחרונים</option>
                <option value={30}>30 ימים אחרונים</option>
                <option value={90}>90 ימים אחרונים</option>
              </select>
            </div>

            <button
              type="button"
              onClick={fetchLogs}
              disabled={loadingLogs}
              className="inline-flex h-9 items-center gap-1.5 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3 text-xs font-medium text-foreground hover:bg-[hsl(var(--surface-3))] disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${loadingLogs ? 'animate-spin' : ''}`} />
              רענון יומן
            </button>
          </div>

          {/* Activity Statistics Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">סה"כ אירועים שנוטרו</span>
              <p className="mt-1 font-mono text-2xl font-bold text-foreground">
                {loadingLogs ? '...' : logsData?.stats.totalEvents ?? 0}
              </p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">הגשות טפסים שנקלטו</span>
              <p className="mt-1 font-mono text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {loadingLogs ? '...' : logsData?.stats.totalSubmissions ?? 0}
              </p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">אירועי אבטחה ו-OTP</span>
              <p className="mt-1 font-mono text-2xl font-bold text-primary">
                {loadingLogs ? '...' : logsData?.stats.totalSecurityEvents ?? 0}
              </p>
            </div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4">
              <span className="text-[11px] font-medium text-muted-foreground">עדכוני סטטוס ואישורים</span>
              <p className="mt-1 font-mono text-2xl font-bold text-foreground">
                {loadingLogs ? '...' : logsData?.stats.totalStatusUpdates ?? 0}
              </p>
            </div>
          </div>

          {/* Activity Stream Feed */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">
                יומן פעילות וביקורת ({filteredLogs.length} אירועים)
              </h2>
              {logsData && (
                <span className="text-xs text-muted-foreground">
                  נוטרו {logsData.scannedBoardsCount} לוחות פעילים
                </span>
              )}
            </div>

            {loadingLogs ? (
              <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-12 text-center text-xs text-muted-foreground">
                <RefreshCw className="mx-auto mb-2 size-5 animate-spin text-primary" />
                טוען נתוני Activity Logs מלוחות monday...
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="border border-dashed border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-12 text-center text-xs text-muted-foreground">
                לא נמצאו רשומות פעילות בטווח התאריכים והסינון שנבחרו.
              </div>
            ) : (
              <div className="space-y-2.5">
                {filteredLogs.map((log) => {
                  const isSubmission = log.eventCategory === 'submission';
                  const isSecurity = log.eventCategory === 'security';
                  const isStatus = log.eventCategory === 'status_update';

                  return (
                    <div
                      key={log.id}
                      className="flex flex-col gap-2 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4 transition-colors hover:bg-[hsl(var(--surface-2))] sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {isSubmission ? (
                            <FileCheck2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                          ) : isSecurity ? (
                            <KeyRound className="size-4 text-primary" />
                          ) : isStatus ? (
                            <TrendingUp className="size-4 text-amber-600 dark:text-amber-400" />
                          ) : (
                            <Activity className="size-4 text-muted-foreground" />
                          )}
                        </div>

                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold text-foreground">{log.eventTitle}</span>
                            <span
                              className={`px-1.5 py-0.5 text-[10px] font-medium border ${
                                isSubmission
                                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                  : isSecurity
                                    ? 'border-primary/40 bg-primary/10 text-primary'
                                    : isStatus
                                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300'
                                      : 'border-[hsl(var(--rule))] bg-[hsl(var(--surface-3))] text-muted-foreground'
                              }`}
                            >
                              {log.boardName}
                            </span>
                          </div>

                          <p className="mt-1 text-xs text-muted-foreground">{log.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground sm:flex-col sm:items-end">
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="size-3" />
                          {new Date(log.createdAt).toLocaleString('he-IL', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </span>
                        {log.userId ? (
                          <span className="text-[10px]">מזהה מבצע: {log.userId}</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
